import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Award, Users, Check, X, Loader2, Send, ArrowLeft, Activity, Dumbbell, TrendingUp, MessageCircle, NotebookPen, Pin, PinOff, Pencil, Trash2, Lock } from "lucide-react";
import WeightTrackerChart from "../components/WeightTrackerChart";
import { toast } from "sonner";

/**
 * Trainer Hub — landing page for users with is_trainer=true.
 *
 * Sections:
 *   - Pending incoming requests (Accept/Decline)
 *   - Active clients list (drills into per-client details)
 *   - Per-client detail panel: progress stats, sessions list, workouts list,
 *     "Suggest Workout" button (picks from trainer's own workout library)
 *
 * Live realtime hooks:
 *   - trainer:request (new incoming request → toast + refresh inbox)
 *   - trainer:suggestion_accepted (client accepted a suggestion → toast)
 *   - trainer:client_disconnected (client unlinked → toast + refresh)
 */
export default function TrainerHub() {
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();

  const [incoming, setIncoming] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inc, cl] = await Promise.all([
        api.get("/trainer-requests/incoming"),
        api.get("/trainer/clients"),
      ]);
      setIncoming(inc.data || []);
      setClients(cl.data || []);
    } catch {
      /* tolerate */
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsubs = [
      subscribe("trainer:request", (m) => {
        toast.success(`New trainer request from ${m.from_client_name || "a user"}`);
        load();
      }),
      subscribe("trainer:suggestion_accepted", (m) => {
        toast.success(`${m.client_name || "Your client"} accepted your workout suggestion 💪`);
      }),
      subscribe("trainer:client_disconnected", (m) => {
        toast(`${m.client_name || "A client"} disconnected`);
        load();
      }),
    ];
    return () => unsubs.forEach((u) => u && u());
  }, [subscribe, load]);

  const accept = async (rid) => {
    try {
      await api.post(`/trainer-requests/${rid}/accept`);
      toast.success("Request accepted — client is now connected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept");
    }
  };
  const decline = async (rid) => {
    try {
      await api.post(`/trainer-requests/${rid}/decline`);
      toast("Request declined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't decline");
    }
  };

  if (!user) return null;
  if (!user.is_trainer) {
    return (
      <div data-testid="trainer-hub-cta" className="max-w-2xl mx-auto p-8 text-center">
        <Award className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
        <h1 className="font-display text-2xl uppercase mb-2">Trainer Hub</h1>
        <p data-testid="trainer-hub-enable-copy" className="text-sm text-neutral-400 mb-4">You haven't enabled Trainer mode yet — turn it on in your Profile.</p>
        <Link to="/profile">
          <Button data-testid="enable-trainer-from-hub-link" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display">
            Enable in Profile →
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="trainer-hub" className="max-w-5xl mx-auto p-4 pb-24">
      <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.3em] text-blue-500 font-bold">
        <Award className="w-3 h-3" /> Trainer Hub
      </div>
      <h1 className="font-display text-4xl uppercase mb-6">Your Roster</h1>

      {incoming.length > 0 && (
        <div data-testid="incoming-requests-block" className="border border-blue-500/40 bg-blue-500/5 p-5 mb-6">
          <h2 className="font-display text-xl uppercase mb-3">Pending requests</h2>
          <div className="space-y-2">
            {incoming.map((r) => (
              <div key={r.request_id} data-testid={`incoming-request-${r.request_id}`} className="border border-neutral-800 bg-neutral-950 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base">{r.from_client_name || "A client"}</div>
                    {r.message && <p className="text-sm text-neutral-300 mt-1 italic">"{r.message}"</p>}
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button data-testid={`accept-request-${r.request_id}`} size="sm" onClick={() => accept(r.request_id)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                      <Check className="w-4 h-4 mr-1.5" /> Accept
                    </Button>
                    <Button data-testid={`decline-request-${r.request_id}`} size="sm" variant="ghost" onClick={() => decline(r.request_id)} className="text-neutral-400 hover:text-white rounded-none uppercase tracking-wider font-display h-9">
                      <X className="w-4 h-4 mr-1.5" /> Decline
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-neutral-800 p-5">
        <h2 className="font-display text-xl uppercase mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" /> Your clients ({clients.length})
        </h2>
        {clients.length === 0 ? (
          <p className="text-xs text-neutral-500">No clients yet — when someone sends you a request, accept it above to start coaching.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {clients.map((c) => (
              <button
                key={c.client_user_id}
                data-testid={`client-row-${c.client_user_id}`}
                onClick={() => setSelectedClient(c)}
                className="text-left border border-neutral-800 hover:border-blue-500 p-3 bg-neutral-950 transition-colors"
              >
                <div className="font-display text-base">{c.client_name}</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">
                  Since {new Date(c.connected_since).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Client detail dialog */}
      {selectedClient && (
        <ClientDetailDialog
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
}

function ClientDetailDialog({ client, onClose }) {
  const [progress, setProgress] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, w] = await Promise.all([
          api.get(`/trainer/clients/${client.client_user_id}/progress`),
          api.get(`/trainer/clients/${client.client_user_id}/sessions`),
          api.get(`/trainer/clients/${client.client_user_id}/workouts`),
        ]);
        setProgress(p.data);
        setSessions(s.data || []);
        setWorkouts(w.data || []);
      } catch {
        /* tolerate */
      } finally {
        setLoading(false);
      }
    })();
  }, [client.client_user_id]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <button onClick={onClose} data-testid="client-detail-back" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back to roster
          </button>
          <DialogTitle data-testid="client-detail-name" className="font-display text-2xl uppercase">{client.client_name}</DialogTitle>
          <DialogDescription className="text-xs text-neutral-500 tracking-widest uppercase">
            Read-only — workouts, sessions, progress. Suggest a workout to push it into their plan.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-neutral-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading client data…
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="client-progress-block" className="grid grid-cols-3 gap-2">
              <Stat icon={Activity} label="Sessions (30d)" value={progress?.sessions_last_30d ?? 0} />
              <Stat icon={Dumbbell} label="Volume kg (30d)" value={progress?.total_volume_kg_30d ?? 0} />
              <Stat icon={TrendingUp} label="Cardio km (30d)" value={progress?.total_cardio_km_30d ?? 0} />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button data-testid="suggest-workout-btn" onClick={() => setSuggestOpen(true)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Send className="w-3 h-3 mr-1.5" /> Suggest Workout
              </Button>
              <Link to={`/social?dm=${client.client_user_id}`}>
                <Button data-testid="message-client-btn" variant="outline" className="border-neutral-700 hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-9">
                  <MessageCircle className="w-3 h-3 mr-1.5" /> Message
                </Button>
              </Link>
            </div>

            <div data-testid="client-sessions-block">
              <h3 className="font-display text-base uppercase mb-2">Recent sessions</h3>
              {sessions.length === 0 ? (
                <p className="text-xs text-neutral-500">No completed sessions yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {sessions.slice(0, 10).map((s) => (
                    <div key={s.session_id} className="flex items-center justify-between border-b border-neutral-900 py-1.5 text-sm">
                      <span className="truncate">{s.workout_name || "—"}</span>
                      <span className="text-xs text-neutral-500">{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="client-workouts-block">
              <h3 className="font-display text-base uppercase mb-2">Their workouts</h3>
              {workouts.length === 0 ? (
                <p className="text-xs text-neutral-500">No workouts yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {workouts.slice(0, 10).map((w) => (
                    <div key={w.workout_id} className="text-sm border-b border-neutral-900 py-1.5">{w.name || "Untitled"}</div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="client-weight-chart-block">
              <h3 className="font-display text-base uppercase mb-2">Weight progress</h3>
              <WeightTrackerChart userId={client.client_user_id} readOnly />
            </div>

            <TrainerNotesBlock clientUserId={client.client_user_id} />
          </div>
        )}

        {suggestOpen && (
          <SuggestWorkoutDialog
            client={client}
            onClose={() => setSuggestOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-neutral-800 p-3 bg-neutral-950 text-center">
      <Icon className="w-4 h-4 text-blue-500 mx-auto" />
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
      <div className="font-display text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function TrainerNotesBlock({ clientUserId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/trainer/clients/${clientUserId}/notes`);
      // Pinned notes first, then newest.
      const sorted = [...(data || [])].sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setNotes(sorted);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [clientUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.post(`/trainer/clients/${clientUserId}/notes`, { text });
      setDraft("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add note");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n) => {
    setEditingId(n.note_id);
    setEditText(n.text);
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      await api.put(`/trainer/clients/${clientUserId}/notes/${editingId}`, { text: editText.trim() });
      setEditingId(null);
      setEditText("");
      toast.success("Note updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  const togglePin = async (n) => {
    try {
      await api.put(`/trainer/clients/${clientUserId}/notes/${n.note_id}`, { text: n.text, pinned: !n.pinned });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't pin");
    }
  };

  const del = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/trainer/clients/${clientUserId}/notes/${nid}`);
      toast("Note deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div data-testid="trainer-notes-block">
      <div className="flex items-center gap-2 mb-2">
        <NotebookPen className="w-4 h-4 text-blue-500" />
        <h3 className="font-display text-base uppercase">Private notes</h3>
        <span className="inline-flex items-center text-[10px] uppercase tracking-widest text-neutral-500">
          <Lock className="w-3 h-3 mr-1" /> Visible only to you
        </span>
      </div>
      <div className="border border-neutral-800 bg-neutral-950 p-3">
        <textarea
          data-testid="new-note-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder="Form cues, weekly observations, injury watch…"
          className="w-full bg-neutral-900 border border-neutral-800 text-white p-2 text-sm rounded-none focus:outline-none focus:border-blue-500"
        />
        <div className="flex justify-end mt-2">
          <Button
            data-testid="add-note-btn"
            size="sm"
            onClick={add}
            disabled={!draft.trim() || saving}
            className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <NotebookPen className="w-3.5 h-3.5 mr-1.5" />}
            Add note
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {loading ? (
          <div className="text-xs text-neutral-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading notes…</div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-neutral-500">No notes yet — jot down anything that'll help next session.</p>
        ) : notes.map((n) => (
          <div
            key={n.note_id}
            data-testid={`note-${n.note_id}`}
            className={`border p-3 bg-neutral-950 ${n.pinned ? "border-blue-500/40" : "border-neutral-800"}`}
          >
            {editingId === n.note_id ? (
              <>
                <textarea
                  data-testid={`edit-note-input-${n.note_id}`}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                  maxLength={4000}
                  className="w-full bg-neutral-900 border border-neutral-800 text-white p-2 text-sm rounded-none focus:outline-none focus:border-blue-500"
                />
                <div className="flex gap-1.5 justify-end mt-2">
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditText(""); }} className="text-neutral-400 hover:text-white rounded-none uppercase tracking-wider font-display h-8">Cancel</Button>
                  <Button size="sm" data-testid={`save-note-btn-${n.note_id}`} onClick={saveEdit} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">Save</Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm whitespace-pre-wrap break-words">{n.text}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {n.pinned && <span className="text-blue-500 mr-2">Pinned</span>}
                    {new Date(n.created_at).toLocaleString()}
                    {n.updated_at && n.updated_at !== n.created_at && " · edited"}
                  </div>
                  <div className="flex gap-0.5">
                    <Button data-testid={`pin-note-btn-${n.note_id}`} size="icon" variant="ghost" onClick={() => togglePin(n)} className="text-neutral-500 hover:text-blue-500 h-7 w-7" title={n.pinned ? "Unpin" : "Pin"}>
                      {n.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                    </Button>
                    <Button data-testid={`edit-note-btn-${n.note_id}`} size="icon" variant="ghost" onClick={() => startEdit(n)} className="text-neutral-500 hover:text-white h-7 w-7" title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-note-btn-${n.note_id}`} size="icon" variant="ghost" onClick={() => del(n.note_id)} className="text-neutral-500 hover:text-blue-500 h-7 w-7" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestWorkoutDialog({ client, onClose }) {
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [selectedWid, setSelectedWid] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/workouts");
        setMyWorkouts(data || []);
      } catch {
        /* tolerate */
      }
    })();
  }, []);

  const send = async () => {
    if (!selectedWid) return;
    setSending(true);
    try {
      await api.post(`/trainer/clients/${client.client_user_id}/suggest-workout`, {
        workout_id: selectedWid,
        message: message.trim() || null,
      });
      toast.success(`Workout suggested to ${client.client_name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send suggestion");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase">Suggest a workout</DialogTitle>
          <DialogDescription className="text-xs text-neutral-500 tracking-widest uppercase">
            Pick one of your own workouts. {client.client_name} will get an in-app + push notification — they must accept before it shows in their plan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Pick a workout</Label>
            <div className="mt-2 max-h-64 overflow-y-auto border border-neutral-800">
              {myWorkouts.length === 0 ? (
                <div className="p-4 text-sm text-neutral-500">No workouts in your library yet. <Link to="/workouts/new" className="text-blue-500">Create one →</Link></div>
              ) : (
                myWorkouts.map((w) => (
                  <button
                    key={w.workout_id}
                    data-testid={`suggest-pick-${w.workout_id}`}
                    onClick={() => setSelectedWid(w.workout_id)}
                    className={`block w-full text-left p-2.5 text-sm border-b border-neutral-900 last:border-b-0 ${selectedWid === w.workout_id ? "bg-blue-500/10 border-l-2 border-l-blue-500" : "hover:bg-neutral-900"}`}
                  >
                    {w.name || "Untitled"}
                  </button>
                ))
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Note (optional)</Label>
            <Textarea
              data-testid="suggest-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={400}
              rows={3}
              placeholder="Add this to your week — focus on bracing on the squats."
              className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none"
            />
          </div>
          <Button
            data-testid="suggest-send-btn"
            onClick={send}
            disabled={!selectedWid || sending}
            className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Suggestion
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Local Label since we don't re-import from above.
const Label = ({ className = "", children }) => <label className={className}>{children}</label>;
